import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { BayenahLogo } from '@/components/brand/BayenahLogo';
import { EndOfServiceContent, ContractReviewContent } from '@/components/blog/BlogContent';

// Blog post data - exported for use in BlogIndex
export const blogPosts = {
  'مكافأة-نهاية-الخدمة': {
    title: 'كيف تحسب مكافأة نهاية الخدمة في السعودية؟',
    subtitle: 'الدليل الشامل لحساب المكافأة في القطاع الخاص وفقاً لنظام العمل السعودي — المواد 84، 85، 86، 87، 88',
    date: 'مارس 2026',
    dateISO: '2026-03-27',
    metaTitle: 'كيف تحسب مكافأة نهاية الخدمة في السعودية؟ — الدليل الشامل 2026 | بينة',
    metaDescription: 'دليل شامل لحساب مكافأة نهاية الخدمة في القطاع الخاص وفقاً لنظام العمل السعودي. المواد 84، 85، 86، 87، 88 مع أمثلة عملية وجدول نسب الاستحقاق.',
    excerpt: 'دليل شامل لحساب مكافأة نهاية الخدمة في القطاع الخاص وفقاً لنظام العمل السعودي مع أمثلة عملية.',
    ContentComponent: EndOfServiceContent,
  },
  'مراجعة-عقد-تجاري': {
    title: '10 بنود يجب مراجعتها قبل توقيع أي عقد تجاري في السعودية',
    subtitle: 'دليل عملي لأصحاب الشركات والمديرين التنفيذيين ومسؤولي المشتريات — وفقاً لنظام المعاملات المدنية ونظام المحاكم التجارية',
    date: 'مارس 2026',
    dateISO: '2026-03-28',
    metaTitle: '10 بنود يجب مراجعتها قبل توقيع أي عقد تجاري في السعودية | بينة',
    metaDescription: 'دليل عملي لمراجعة العقود التجارية قبل التوقيع. 10 بنود حرجة يجب التحقق منها وفقاً لنظام المعاملات المدنية ونظام المحاكم التجارية في المملكة العربية السعودية.',
    excerpt: 'دليل عملي لمراجعة العقود التجارية. 10 بنود حرجة يجب التحقق منها وفقاً للأنظمة السعودية.',
    ContentComponent: ContractReviewContent,
  },
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts[slug];

  useEffect(() => {
    if (post) {
      document.title = post.metaTitle;
      
      // Update meta tags
      const updateMeta = (name, content, isProperty = false) => {
        const attr = isProperty ? 'property' : 'name';
        let meta = document.querySelector(`meta[${attr}="${name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attr, name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      updateMeta('description', post.metaDescription);
      updateMeta('og:title', post.metaTitle, true);
      updateMeta('og:description', post.metaDescription, true);
      updateMeta('og:url', `https://bayenah.com/blog/${slug}`, true);
      updateMeta('og:type', 'article', true);
      updateMeta('twitter:title', post.metaTitle);
      updateMeta('twitter:description', post.metaDescription);

      // Add Article schema
      const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description: post.metaDescription,
        datePublished: post.dateISO,
        dateModified: post.dateISO,
        author: {
          '@type': 'Organization',
          name: 'Bayenah',
          url: 'https://bayenah.com'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Bayenah',
          logo: {
            '@type': 'ImageObject',
            url: 'https://bayenah.com/branding/bayenah-logo.svg'
          }
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://bayenah.com/blog/${slug}`
        },
        inLanguage: 'ar'
      };

      let script = document.getElementById('article-schema');
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'article-schema';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(articleSchema);

      // Set RTL
      document.documentElement.lang = 'ar';
      document.documentElement.dir = 'rtl';
    }

    window.scrollTo(0, 0);

    // Cleanup schema on unmount
    return () => {
      const script = document.getElementById('article-schema');
      if (script) script.remove();
    };
  }, [slug, post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F7F7F5] text-white flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">المقال غير موجود</h1>
          <Link to="/blog" className="text-[#111111] hover:underline">العودة إلى المدونة</Link>
        </div>
      </div>
    );
  }

  const ContentComponent = post.ContentComponent;

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-white" dir="rtl">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#F7F7F5]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2 text-white/70 hover:text-[#111111] transition-colors text-sm">
            <ArrowRight className="w-4 h-4" />
            <span>المدونة</span>
          </Link>
          <Link to="/">
            <BayenahLogo height={28} variant="inverted" />
          </Link>
        </div>
      </header>

      {/* Article Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <article className="blog-article">
          {/* Article Header */}
          <header className="mb-12 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight font-arabic">
              {post.title}
            </h1>
            <p className="text-lg md:text-xl text-white/60 mb-4 leading-relaxed font-arabic">
              {post.subtitle}
            </p>
            <p className="text-sm text-white/40 font-arabic">آخر تحديث: {post.date}</p>
          </header>

          {/* Article Body */}
          <div className="blog-content prose prose-invert max-w-none">
            <ContentComponent />
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/">
            <BayenahLogo height={24} variant="inverted" />
          </Link>
          <p className="text-xs text-white/40">© 2026 بينة. جميع الحقوق محفوظة.</p>
        </div>
      </footer>

      {/* Blog-specific styles */}
      <style>{`
        .blog-content {
          font-family: 'Cairo', sans-serif;
          font-size: 1.125rem;
          line-height: 1.9;
          color: rgba(255, 255, 255, 0.85);
        }

        .blog-content h2 {
          font-size: 1.75rem;
          font-weight: 700;
          color: #ffffff;
          margin-top: 3rem;
          margin-bottom: 1.25rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid rgba(0, 0, 0, 0.2);
        }

        .blog-content h3 {
          font-size: 1.35rem;
          font-weight: 600;
          color: #ffffff;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }

        .blog-content p {
          margin-bottom: 1.25rem;
        }

        .blog-content strong {
          color: #ffffff;
          font-weight: 600;
        }

        .blog-content blockquote {
          margin: 2rem 0;
          padding: 1.5rem 2rem;
          background: rgba(0, 0, 0, 0.05);
          border-right: 4px solid #111111;
          border-radius: 0 12px 12px 0;
          font-style: normal;
          color: rgba(255, 255, 255, 0.9);
        }

        .blog-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
          background: rgba(255, 255, 255, 0.02);
          border-radius: 12px;
          overflow: hidden;
        }

        .blog-content th {
          background: rgba(0, 0, 0, 0.1);
          color: #111111;
          font-weight: 600;
          padding: 1rem;
          text-align: right;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .blog-content td {
          padding: 1rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          color: rgba(255, 255, 255, 0.8);
        }

        .blog-content tr:last-child td {
          border-bottom: none;
        }

        .blog-content tr:hover td {
          background: rgba(255, 255, 255, 0.02);
        }

        .blog-content a {
          color: #111111;
          text-decoration: none;
        }

        .blog-content a:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}
